import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { 
  Idea, 
  RoadmapItem, 
  Task, 
  User, 
  Notification, 
  AIAnalysis,
  AppSettings,
  ViewMode,
  FilterOptions,
  SortOptions
} from '../types';
import { supabase } from '../lib/supabase';
import { determineAuthMethod, canUseBackdoorAccess } from '../lib/authRouter';
import { sendNewIdeaNotification } from '../lib/email';

console.log('📧 EMAIL_IMPORT_SUCCESS: Email import successful in SupabaseAppContext');

// Helper functions to generate unique IDs
const generateNextIdeaId = (ideas: Idea[]): string => {
  const existingNumbers = ideas
    .map(idea => idea.uniqueId?.match(/^I(\d+)$/)?.[1])
    .filter(num => num !== undefined)
    .map(num => parseInt(num as string, 10))
    .filter(num => !isNaN(num));

  const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
  const nextNumber = maxNumber + 1;
  return `I${nextNumber.toString().padStart(6, '0')}`;
};

const generateNextTaskId = (tasks: Task[]): string => {
  const existingNumbers = tasks
    .map(task => task.uniqueId?.match(/^T(\d+)$/)?.[1])
    .filter(num => num !== undefined)
    .map(num => parseInt(num as string, 10))
    .filter(num => !isNaN(num));

  const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
  const nextNumber = maxNumber + 1;
  return `T${nextNumber.toString().padStart(6, '0')}`;
};

// Helper functions to transform database data to app format
const transformIdea = (dbIdea: any, existingIdeas: Idea[] = []): Idea => ({
  id: dbIdea.id,
  uniqueId: dbIdea.unique_id || generateNextIdeaId(existingIdeas),
  title: dbIdea.title,
  description: dbIdea.description,
  category: dbIdea.category,
  status: dbIdea.status,
  owner: {
    id: dbIdea.owner?.id || dbIdea.owner_id,
    name: dbIdea.owner?.name || 'Unknown User',
    email: dbIdea.owner?.email || '',
    avatar: dbIdea.owner?.avatar || '👤'
  },
  effort: dbIdea.effort,
  value: dbIdea.value,
  score: dbIdea.score || (dbIdea.value / dbIdea.effort),
  tags: dbIdea.tags || [],
  plannedQuarter: dbIdea.planned_quarter || undefined,
  dateSubmitted: new Date(dbIdea.created_at),
  dateUpdated: new Date(dbIdea.updated_at),
  comments: dbIdea.comments?.map((comment: any) => ({
    id: comment.id,
    content: comment.content,
    author: {
      id: comment.author?.id || comment.author_id,
      name: comment.author?.name || 'Unknown User',
      email: comment.author?.email || '',
      avatar: comment.author?.avatar || '👤'
    },
    timestamp: new Date(comment.created_at),
    mentions: comment.mentions || []
  })) || [],
  votes: dbIdea.votes?.map((vote: any) => ({
    id: vote.id,
    user: {
      id: vote.user_id,
      name: 'User',
      email: '',
      avatar: '👤'
    },
    type: vote.type,
    timestamp: new Date(vote.created_at)
  })) || [],
  attachments: []
});

const transformTask = (dbTask: any, existingTasks: Task[] = []): Task => ({
  id: dbTask.id,
  uniqueId: dbTask.unique_id || generateNextTaskId(existingTasks),
  title: dbTask.title,
  description: dbTask.description || '',
  status: dbTask.status,
  priority: dbTask.priority,
  assignee: dbTask.assignee ? {
    id: dbTask.assignee.id,
    name: dbTask.assignee.name,
    email: dbTask.assignee.email,
    avatar: dbTask.assignee.avatar || '👤'
  } : undefined,
  linkedIdeaId: dbTask.linked_idea_id,
  linkedRoadmapId: dbTask.linked_roadmap_id,
  dueDate: dbTask.due_date ? new Date(dbTask.due_date) : undefined,
  dateCreated: new Date(dbTask.created_at),
  dateUpdated: new Date(dbTask.updated_at),
  comments: dbTask.comments?.map((comment: any) => ({
    id: comment.id,
    content: comment.content,
    author: {
      id: comment.author?.id || comment.author_id,
      name: comment.author?.name || 'Unknown User',
      email: comment.author?.email || '',
      avatar: comment.author?.avatar || '👤'
    },
    timestamp: new Date(comment.created_at),
    mentions: comment.mentions || []
  })) || [],
  attachments: []
});

const transformRoadmapItem = (dbItem: any): RoadmapItem => ({
  id: dbItem.id,
  ideaId: dbItem.idea_id,
  title: dbItem.title,
  description: dbItem.description || '',
  status: dbItem.status,
  assignee: dbItem.assignee ? {
    id: dbItem.assignee.id,
    name: dbItem.assignee.name,
    email: dbItem.assignee.email,
    avatar: dbItem.assignee.avatar || '👤'
  } : undefined,
  startDate: dbItem.start_date ? new Date(dbItem.start_date) : undefined,
  endDate: dbItem.end_date ? new Date(dbItem.end_date) : undefined,
  quarter: dbItem.quarter,
  priority: dbItem.priority,
  category: dbItem.category,
  progress: dbItem.progress || 0
});

// Email whitelist - Add your colleagues' email addresses here
// Only users with emails in this list can sign up or sign in
const ALLOWED_EMAILS = [
  // External emails
  'cfrancosky@gmail.com',
  'cfrancosky@outlook.com',
  'chris0f0@hotmail.com',
  'info@synthshare.ai',
  
  // Kajeet team members
  'aradhakrishnan@kajeet.com',
  'aschaible@kajeet.com',
  'areddy@kajeet.com',
  'bpeters@kajeet.com',
  'cfrancosky@kajeet.com',
  'dmech@kajeet.com',
  'dmiller@kajeet.com',
  'dpinto@kajeet.com',
  'drittenhouse@kajeet.com',
  'gabramovitz@kajeet.com',
  'gjones@kajeet.com',
  'gnoakes@kajeet.com',
  'gzukowski@kajeet.com',
  'jason@kajeet.com',
  'ksnead@kajeet.com',
  'lgarner@kajeet.com',
  'mcaplan@kajeet.com',
  'mmitchell@kajeet.com',
  'mprasad@kajeet.com',
  'msim@kajeet.com',
  'radams@kajeet.com',
  'tlehn@kajeet.com',
  'tgregor@kajeet.com',
  // Add more emails as needed
];

// TEMPORARY DEBUG FLAG - Set to true to bypass profile queries for testing
const DEBUG_BYPASS_PROFILE_QUERIES = false;

// Helper function to check if email is allowed
const isEmailAllowed = (email: string): boolean => {
  return ALLOWED_EMAILS.includes(email.toLowerCase().trim());
};

interface AppState {
  ideas: Idea[];
  roadmapItems: RoadmapItem[];
  tasks: Task[];
  users: User[];
  notifications: Notification[];
  aiAnalyses: AIAnalysis[];
  settings: AppSettings;
  viewMode: ViewMode;
  filters: FilterOptions;
  sortOptions: SortOptions;
  currentUser: User | null;
  searchQuery: string;
  loading: boolean;
  error: string | null;
  authInitialized: boolean; // Simple flag for when auth is ready
}

interface AppContextType {
  state: AppState;
  actions: {
    loadData: () => Promise<void>;
    signIn: (email: string, password?: string) => Promise<void>;
    signInWithSSO: (email: string) => Promise<void>;
    signInWithAzure: () => Promise<void>;
    signInWithOAuth: (provider: string) => Promise<void>;
    signUp: (email: string, name: string) => Promise<void>;
    signOut: () => Promise<void>;
    updateProfile: (name: string, avatar?: string) => Promise<void>;
    addIdea: (idea: Omit<Idea, 'id' | 'uniqueId' | 'dateSubmitted' | 'dateUpdated' | 'comments' | 'votes' | 'score' | 'attachments' | 'owner'>) => Promise<void>;
    updateIdea: (idea: Idea) => Promise<void>;
    deleteIdea: (id: string) => Promise<void>;
    addRoadmapItem: (item: Omit<RoadmapItem, 'id'>) => Promise<void>;
    updateRoadmapItem: (item: RoadmapItem) => Promise<void>;
    deleteRoadmapItem: (id: string) => Promise<void>;
    addTask: (task: Omit<Task, 'id' | 'uniqueId' | 'dateCreated' | 'dateUpdated' | 'comments' | 'attachments'>) => Promise<void>;
    updateTask: (task: Task) => Promise<void>;
    deleteTask: (id: string) => Promise<void>;
    voteOnIdea: (ideaId: string, voteType: 'up' | 'down') => Promise<void>;
    addComment: (ideaId: string, content: string, mentions?: string[]) => Promise<void>;
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => Promise<void>;
    triggerAIAnalysis: (ideaId: string) => Promise<void>;
    setViewMode: (viewMode: Partial<ViewMode>) => void;
    setFilters: (filters: Partial<FilterOptions>) => void;
    setSortOptions: (sortOptions: SortOptions) => void;
    setSearchQuery: (query: string) => void;
  };
}

const initialState: AppState = {
  ideas: [],
  roadmapItems: [],
  tasks: [],
  users: [],
  notifications: [],
  aiAnalyses: [],
  settings: {
    notifications: {
      email: true,
      inApp: true,
      mentions: true,
      comments: true,
      statusChanges: true,
    },
    display: {
      theme: 'light',
      compactMode: false,
    },
  },
  viewMode: {
    roadmap: 'timeline',
    ideas: 'list',
  },
  filters: {
    categories: [],
    statuses: [],
    owners: [],
    tags: [],
    effortRange: [1, 10],
    valueRange: [1, 10],
  },
  sortOptions: {
    field: 'dateSubmitted',
    direction: 'desc',
  },
  currentUser: null,
  searchQuery: '',
  loading: false, // Start simple - no loading
  error: null,
  authInitialized: false, // Auth not ready yet
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const SupabaseAppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>(initialState);


  // Load data from Supabase
  const isLoadingDataRef = useRef(false);
  
  const loadData = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoadingDataRef.current) {
      return;
    }
    isLoadingDataRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // First do a simple connection test
      try {
        // Skip the database connection test since auth is already working
        // The hanging issue was caused by RLS policies on the profiles table
      } catch (err: any) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: `Cannot connect to database. Please check your network connection and try again. Error: ${err.message}`
        }));
        return;
      }
      
      // Set a timeout to prevent hanging
      let timeoutStep = 'unknown';
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Loading data timed out at step: ${timeoutStep}`));
        }, 30000);
      });
      
      // Get current user with timeout
      timeoutStep = 'getting current user';
      
      // Use getSession instead of getUser to avoid the hanging issue
      const getSessionPromise = supabase.auth.getSession();
      
      const sessionResult = await Promise.race([
        getSessionPromise,
        timeoutPromise
      ]) as any;
      
      const user = sessionResult?.data?.session?.user;
      let currentUser: User | null = null;

      if (user) {
        if (DEBUG_BYPASS_PROFILE_QUERIES) {
          // Create a proper name to avoid triggering FirstTimeSetup
          const emailPrefix = user.email?.split('@')[0] || 'User';
          const properName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1) + ' (Debug)';
          
          currentUser = {
            id: user.id,
            name: properName, // Use a proper name that won't trigger needsSetup
            email: user.email || '',
            avatar: '👤'
          };
        } else {
          // Get profile with timeout
          timeoutStep = 'loading user profile';
          
          const getProfilePromise = supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
            
          const profileResult = await Promise.race([
            getProfilePromise,
            timeoutPromise
          ]) as any;

          const { data: profile } = profileResult;
          if (profile) {
            currentUser = {
              id: profile.id,
              name: profile.name || profile.email.split('@')[0] || 'User',
              email: profile.email,
              avatar: profile.avatar || '👤'
            };
          } else {
            // Create a basic profile if none exists or profile query failed
            console.log('📝 No profile found, creating profile for user:', user.email);
            
            // Try to create the profile in the database
            try {
              const { error: upsertError } = await supabase
                .from('profiles')
                .upsert({
                  id: user.id,
                  email: user.email || '',
                  name: user.email?.split('@')[0] || 'User',
                  avatar: '👤'
                }, { onConflict: 'id' });
                
              if (upsertError) {
                console.error('❌ Profile upsert error:', upsertError);
              } else {
                console.log('✅ Profile created successfully for:', user.email);
              }
            } catch (upsertErr) {
              console.error('❌ Profile upsert exception:', upsertErr);
            }
            
            // Create currentUser regardless of database operation result
            currentUser = {
              id: user.id,
              name: user.email?.split('@')[0] || 'User',
              email: user.email || '',
              avatar: '👤'
            };
          }
        }
      } else {
        console.log('No user found, skipping profile loading');
      }

      // Load all data in parallel with timeout protection
      console.log('Current user for data loading:', currentUser ? `${currentUser.name} (${currentUser.id})` : 'none');
      
      if (DEBUG_BYPASS_PROFILE_QUERIES) {
        // Set empty data to test if the auth flow works without database queries
        const transformedData = {
          ideas: [],
          tasks: [],
          roadmapItems: [],
          users: currentUser ? [currentUser] : [],
          notifications: [],
          currentUser,
          loading: false
        };
        
        setState(prev => ({
          ...prev,
          ...transformedData
        }));

        setState(prev => ({
          ...prev,
          loading: false
        }));
        isLoadingDataRef.current = false;
        return;
      }
      
      // Create data loading promises
      const ideasPromise = supabase
        .from('ideas')
        .select(`
          *,
          owner:profiles!ideas_owner_id_fkey(id, name, email, avatar),
          comments:comments!comments_idea_id_fkey(
            id, content, created_at, mentions,
            author:profiles!comments_author_id_fkey(id, name, email, avatar)
          ),
          votes:votes!votes_idea_id_fkey(id, type, user_id, created_at)
        `)
        .order('created_at', { ascending: false });
        
      const tasksPromise = supabase
        .from('tasks')
        .select(`
          *,
          assignee:profiles!tasks_assignee_id_fkey(id, name, email, avatar),
          comments:comments!comments_task_id_fkey(
            id, content, created_at, mentions,
            author:profiles!comments_author_id_fkey(id, name, email, avatar)
          )
        `)
        .order('created_at', { ascending: false });
        
      const roadmapPromise = supabase
        .from('roadmap_items')
        .select(`
          *,
          assignee:profiles!roadmap_items_assignee_id_fkey(id, name, email, avatar)
        `)
        .order('created_at', { ascending: false });
        
      const usersPromise = supabase
        .from('profiles')
        .select('*')
        .order('name');
        
      const notificationsPromise = currentUser ? supabase
        .from('notifications')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false }) : Promise.resolve({ data: [], error: null });
        
      // const dataStartTime = Date.now(); // Timing variable - removed to fix ESLint
      
      // Execute queries one by one to identify which one hangs
      timeoutStep = 'loading ideas';
      const ideasResult = await Promise.race([ideasPromise, timeoutPromise]) as any;
      
      timeoutStep = 'loading tasks';
      const tasksResult = await Promise.race([tasksPromise, timeoutPromise]) as any;
      
      timeoutStep = 'loading roadmap';
      const roadmapResult = await Promise.race([roadmapPromise, timeoutPromise]) as any;
      
      timeoutStep = 'loading users';
      const usersResult = await Promise.race([usersPromise, timeoutPromise]) as any;
      
      timeoutStep = 'loading notifications';
      const notificationsResult = await Promise.race([notificationsPromise, timeoutPromise]) as any;

      // const dataEndTime = Date.now(); // Timing variable - removed to fix ESLint
      // Check for errors
      if (ideasResult.error) {
        throw ideasResult.error;
      }
      if (tasksResult.error) {
        throw tasksResult.error;
      }
      if (roadmapResult.error) {
        throw roadmapResult.error;
      }
      if (usersResult.error) {
        throw usersResult.error;
      }
      if (notificationsResult.error) {
        throw notificationsResult.error;
      }

      // const transformStartTime = Date.now(); // Timing variable - removed to fix ESLint
      
      // Transform and set data
      const transformedData = {
        ideas: ideasResult.data?.map((dbIdea: any, index: number) => {
          const uniqueId = dbIdea.unique_id || `I${(index + 1).toString().padStart(6, '0')}`;
          return { ...transformIdea(dbIdea, []), uniqueId };
        }) || [],
        tasks: tasksResult.data?.map((dbTask: any, index: number) => {
          const uniqueId = dbTask.unique_id || `T${(index + 1).toString().padStart(6, '0')}`;
          return { ...transformTask(dbTask, []), uniqueId };
        }) || [],
        roadmapItems: roadmapResult.data?.map(transformRoadmapItem) || [],
        users: usersResult.data?.map((profile: any) => ({
          id: profile.id,
          name: profile.name || profile.email.split('@')[0] || 'User',
          email: profile.email,
          avatar: profile.avatar || '👤'
        })) || [],
        notifications: notificationsResult.data?.map((notification: any) => ({
          id: notification.id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          read: notification.read,
          timestamp: new Date(notification.created_at),
          relatedId: notification.related_id || undefined,
          relatedType: notification.related_type || undefined
        })) || [],
        currentUser,
        loading: false
      };
      
      // const transformEndTime = Date.now(); // Timing variable - removed to fix ESLint
      setState(prev => ({
        ...prev,
        ...transformedData
      }));

      isLoadingDataRef.current = false;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      isLoadingDataRef.current = false;
    }
  }, []);


  // Simple auth initialization - just listen to Supabase auth state
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔍 Auth state change:', { event, userEmail: session?.user?.email, hasSession: !!session });
      
      // Mark auth as initialized after first event
      setState(prev => ({ ...prev, authInitialized: true }));
      
      if (session?.user) {
        console.log('✅ User session detected:', session.user.email);
        
        // SECURITY: Validate user email against whitelist even after SSO
        if (!isEmailAllowed(session.user.email || '')) {
          console.warn('Unauthorized user attempted access via SSO:', session.user.email);
          // Sign out the unauthorized user
          await supabase.auth.signOut();
          setState(prev => ({
            ...prev,
            currentUser: null,
            ideas: [],
            tasks: [],
            roadmapItems: [],
            users: [],
            notifications: [],
            loading: false,
            error: 'Access denied. This email address is not authorized to access this application. Please contact your administrator.'
          }));
          return;
        }
        
        console.log('🔄 Starting data load for authenticated user...');
        setState(prev => ({ ...prev, loading: true }));
        loadData();
      } else {
        setState(prev => ({
          ...prev,
          currentUser: null,
          ideas: [],
          tasks: [],
          roadmapItems: [],
          users: [],
          notifications: [],
          loading: false,
          error: null
        }));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadData]);



  // Authentication functions
  const signInWithSSO = async (email: string) => {
    const domain = email.split('@')[1];
    
    try {
      const { data, error } = await supabase.auth.signInWithSSO({
        domain: domain,
      });
      
      if (error) throw error;
      
      if (data?.url) {
        // Redirect to the SSO provider
        window.location.href = data.url;
      } else {
        throw new Error('SSO initialization failed. No redirect URL received.');
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithAzure = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email',
          redirectTo: `${window.location.origin}`,
        },
      });

      if (error) throw error;
      
      // The browser will be redirected to Azure OAuth
    } catch (error) {
      throw error;
    }
  };

  // New WorkOS SSO method
  const signInWithOAuth = async (provider: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      console.log(`🔄 Starting ${provider} OAuth authentication...`);

      // Check for existing session and clear it if present
      const { data: existingSession } = await supabase.auth.getSession();
      if (existingSession.session) {
        console.log('🔄 Existing session found, clearing before new OAuth...');
        await supabase.auth.signOut();
        // Small delay to ensure cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'email'
        }
      });

      if (error) {
        console.error(`❌ ${provider} OAuth error:`, error);
        setState(prev => ({ ...prev, loading: false }));
        throw new Error(error.message || `${provider} authentication failed`);
      }

      console.log(`✅ ${provider} OAuth initiated successfully`);

    } catch (error: any) {
      console.error(`❌ OAuth error:`, error);
      setState(prev => ({ ...prev, loading: false }));
      throw new Error(error.message || 'OAuth authentication failed');
    }
  };

  const signIn = async (email: string, password?: string) => {
    // Check if email is in the whitelist
    if (!isEmailAllowed(email)) {
      throw new Error('Access denied. This email address is not authorized to access this application. Please contact your administrator.');
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Clear any existing session before attempting new authentication
      console.log('🔄 Checking for existing session before sign in...');
      const { data: existingSession } = await supabase.auth.getSession();
      if (existingSession.session) {
        console.log('🔄 Existing session found, clearing before new sign in...');
        await supabase.auth.signOut();
        // Small delay to ensure cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // NEW: Azure SSO for enterprise emails (unless password provided for backdoor)
      const authMethod = determineAuthMethod(email);

      if (authMethod === 'workos' && !password) {
        // Enterprise email without password - route to Azure OAuth
        await signInWithOAuth('azure');
        return; // signInWithOAuth redirects, so we don't continue
      }
      
      if (authMethod === 'workos' && password && !canUseBackdoorAccess(email)) {
        // Enterprise email with password but not admin - guide to SSO
        throw new Error('Please use "Sign In with Microsoft" for your @kajeet.com account, or contact an administrator for password access.');
      }
      
      let result: any;
      
      if (password) {
        // Express Mode: Validate team password first
        const teamPassword = process.env.REACT_APP_TEAM_PASSWORD;
        if (!teamPassword) {
          throw new Error('Team password not configured. Please contact your administrator.');
        }
        
        if (password !== teamPassword) {
          throw new Error('Incorrect team password. Please try again.');
        }
        
        // Try password authentication first
        result = await supabase.auth.signInWithPassword({ 
          email, 
          password: teamPassword 
        });
        
        // If user doesn't exist or doesn't have a password, handle appropriately
        if (result.error && result.error.message.includes('Invalid login credentials')) {
          console.log('Password auth failed, attempting to handle existing user or create new account for:', email);
          
          // Try to create new user account with password
          const signUpResult = await supabase.auth.signUp({
            email,
            password: teamPassword,
            options: {
              data: { 
                name: email.split('@')[0] // Use email prefix as initial name
              },
            },
          });
          
          if (signUpResult.error) {
            // If signup fails because user already exists, provide simple solution
            if (signUpResult.error.message.includes('User already registered')) {
              throw new Error('Your account exists but Express Mode is being set up. Please try again in a few minutes, or contact Chris if the issue persists.');
            } else {
              throw new Error(`Failed to create account: ${signUpResult.error.message}`);
            }
          }
          
          // If signup succeeded, try to sign in with the new account
          result = await supabase.auth.signInWithPassword({ 
            email, 
            password: teamPassword 
          });
        }
      } else {
        // For users without password, direct them to use Azure OAuth or Express Mode
        throw new Error('Please use "Sign In with Microsoft" for Azure OAuth authentication, or use Express Mode (Quick Access) with the team password.');
      }
      
      if (result.error) throw result.error;
      
    } catch (error) {
      throw error;
    }
  };



  const signUp = async (email: string, name: string) => {
    // Check if email is in the whitelist
    if (!isEmailAllowed(email)) {
      throw new Error('Access denied. This email address is not authorized to create an account. Please contact your administrator.');
    }

    // For now, since magic links don't work, redirect to Express Mode
    throw new Error('Please use Express Mode (Quick Access) to create your account. Check the box and enter the team password.');
  };

  const signOut = async () => {
    console.log('🚪 Starting sign out process...');

    try {
      // Clear Supabase session
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Supabase sign out error:', error);
        throw error;
      }

      // Clear local storage (additional cleanup)
      try {
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('sb-' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
        sessionStorage.clear();
      } catch (storageError) {
        console.warn('⚠️ Storage cleanup warning:', storageError);
      }

      // Clear application state
      setState(prev => ({
        ...prev,
        currentUser: null,
        ideas: [],
        tasks: [],
        roadmapItems: [],
        users: [],
        notifications: [],
        loading: false,
        error: null
      }));

      console.log('✅ Sign out completed successfully');
    } catch (error) {
      console.error('❌ Sign out failed:', error);
      throw error;
    }
  };

  const updateProfile = async (name: string, avatar?: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    if (DEBUG_BYPASS_PROFILE_QUERIES) {
      // Create updated user object
      const updatedUser: User = {
        id: user.id,
        email: user.email || '',
        name: name,
        avatar: avatar || '👤'
      };
      
      // Update state directly
      setState(prev => ({
        ...prev,
        currentUser: updatedUser,
        users: prev.users.map(u => u.id === user.id ? updatedUser : u)
      }));
      
      return;
    }

    const updateData: any = { 
      id: user.id,
      email: user.email || '',
      name 
    };
    if (avatar) updateData.avatar = avatar;

    // Use upsert to create profile if it doesn't exist
    const { error } = await supabase
      .from('profiles')
      .upsert(updateData, { onConflict: 'id' });

    if (error) throw error;

    // Reload data to reflect the changes
    await loadData();
  };

  // Add idea
  const addIdea = async (idea: Omit<Idea, 'id' | 'uniqueId' | 'dateSubmitted' | 'dateUpdated' | 'comments' | 'votes' | 'score' | 'attachments' | 'owner'>) => {
    console.log('🎯 ADDI_DEA_CALLED:', idea.title);
    console.log('🔍 DEBUG_EMAIL_MODULE_CHECK: sendNewIdeaNotification type:', typeof sendNewIdeaNotification);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('❌ ADD_IDEA_NO_USER: User not authenticated');
      throw new Error('User not authenticated');
    }
    console.log('✅ ADD_IDEA_USER_AUTHENTICATED:', user.email);

    const uniqueId = generateNextIdeaId(state.ideas);
    console.log('🏷️ ADD_IDEA_UNIQUE_ID:', uniqueId);

    const { data, error } = await supabase
      .from('ideas')
      .insert({
        title: idea.title,
        description: idea.description,
        category: idea.category,
        status: idea.status || 'New',
        owner_id: user.id,
        effort: idea.effort,
        value: idea.value,
        tags: idea.tags,
        planned_quarter: idea.plannedQuarter || null,
        unique_id: uniqueId
      })
      .select(`
        *,
        owner:profiles!ideas_owner_id_fkey(id, name, email, avatar)
      `)
      .single();

    if (error) {
      console.error('❌ ADD_IDEA_SUPABASE_ERROR:', error);
      throw error;
    }
    console.log('✅ ADD_IDEA_SUPABASE_SUCCESS: Idea inserted into database');

    // Add to state
    const newIdea = transformIdea(data, state.ideas);
    console.log('🔄 ADD_IDEA_TRANSFORM_SUCCESS: Idea transformed:', newIdea.uniqueId);
    setState(prev => ({
      ...prev,
      ideas: [newIdea, ...prev.ideas]
    }));

    // Send email notification (don't await - fire and forget)
    console.log('🚀 IDEA_CREATED_SUCCESS: Sending email notification for:', newIdea.title);
    try {
      await sendNewIdeaNotification({
        id: newIdea.id,
        uniqueId: newIdea.uniqueId,
        title: newIdea.title,
        description: newIdea.description,
        category: newIdea.category,
        status: newIdea.status,
        owner: newIdea.owner,
        effort: newIdea.effort,
        value: newIdea.value,
        score: newIdea.score || (newIdea.value / newIdea.effort),
        tags: newIdea.tags,
        dateSubmitted: newIdea.dateSubmitted,
      });
      console.log('✅ EMAIL_PROCESS_COMPLETED: Email notification sent successfully');
    } catch (emailError) {
      // Log email error but don't fail the idea creation
      console.error('❌ EMAIL_FAILED: Email notification failed, but idea was created successfully:', emailError);
    }
  };

  // Update idea
  const updateIdea = async (idea: Idea) => {
    try {
      // First, let's check if the idea exists and if we can read it
      const { data: existingIdea, error: readError } = await supabase
        .from('ideas')
        .select('*')
        .eq('id', idea.id)
        .single();
      
      if (readError) {
        throw new Error(`Cannot access idea: ${readError.message}`);
      }
      
      if (!existingIdea) {
        throw new Error('Idea not found');
      }
      
      // Now try the update
      const { data, error } = await supabase
        .from('ideas')
        .update({
          title: idea.title,
          description: idea.description,
          category: idea.category,
          status: idea.status,
          effort: idea.effort,
          value: idea.value,
          tags: idea.tags,
          planned_quarter: idea.plannedQuarter || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', idea.id)
        .select();

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        // TEMPORARY WORKAROUND: Update local state when database update fails due to RLS
        const updatedIdea: Idea = {
          ...idea,
          dateUpdated: new Date()
        };
        
        setState(prev => ({
          ...prev,
          ideas: prev.ideas.map(i => i.id === idea.id ? updatedIdea : i)
        }));
        
        return; // Don't throw error, just continue
      }

      // Update state with the new data
      const updatedIdea: Idea = {
        ...idea,
        dateUpdated: new Date()
      };
      
      setState(prev => ({
        ...prev,
        ideas: prev.ideas.map(i => i.id === idea.id ? updatedIdea : i)
      }));
    } catch (error) {
      throw error;
    }
  };

  // Delete idea
  const deleteIdea = async (id: string) => {
    const { error } = await supabase
      .from('ideas')
      .delete()
      .eq('id', id);

    if (error) throw error;

    setState(prev => ({
      ...prev,
      ideas: prev.ideas.filter(i => i.id !== id)
    }));
  };

  // Other action implementations...
  const addRoadmapItem = async (item: Omit<RoadmapItem, 'id'>) => {
    // Implementation similar to addIdea
    };

  const updateRoadmapItem = async (item: RoadmapItem) => {
    };

  const deleteRoadmapItem = async (id: string) => {
    };

  const addTask = async (task: Omit<Task, 'id' | 'uniqueId' | 'dateCreated' | 'dateUpdated' | 'comments' | 'attachments'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const uniqueId = generateNextTaskId(state.tasks);
    
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        assignee_id: task.assignee?.id || null,
        linked_idea_id: task.linkedIdeaId || null,
        linked_roadmap_id: task.linkedRoadmapId || null,
        due_date: task.dueDate?.toISOString() || null,
        unique_id: uniqueId
      })
      .select(`
        *,
        assignee:profiles!tasks_assignee_id_fkey(id, name, email, avatar)
      `)
      .single();

    if (error) throw error;

    // Add to state
    const newTask = transformTask(data, state.tasks);
    setState(prev => ({
      ...prev,
      tasks: [newTask, ...prev.tasks]
    }));
  };

  const updateTask = async (task: Task) => {
    const { data, error } = await supabase
      .from('tasks')
      .update({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        assignee_id: task.assignee?.id || null,
        linked_idea_id: task.linkedIdeaId || null,
        linked_roadmap_id: task.linkedRoadmapId || null,
        due_date: task.dueDate?.toISOString() || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', task.id)
      .select(`
        *,
        assignee:profiles!tasks_assignee_id_fkey(id, name, email, avatar)
      `)
      .single();

    if (error) throw error;

    // Update state
    const updatedTask = transformTask(data, state.tasks);
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === task.id ? updatedTask : t)
    }));
  };

  const deleteTask = async (id: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;

    setState(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.id !== id)
    }));
  };

  const voteOnIdea = async (ideaId: string, voteType: 'up' | 'down') => {
    };

  const addComment = async (ideaId: string, content: string, mentions?: string[]) => {
    };

  const addNotification = async (notification: Omit<Notification, 'id' | 'timestamp'>) => {
    };

  const triggerAIAnalysis = async (ideaId: string) => {
    };

  // Local state actions
  const setViewMode = (viewMode: Partial<ViewMode>) => {
    setState(prev => ({
      ...prev,
      viewMode: { ...prev.viewMode, ...viewMode }
    }));
  };

  const setFilters = (filters: Partial<FilterOptions>) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters }
    }));
  };

  const setSortOptions = (sortOptions: SortOptions) => {
    setState(prev => ({
      ...prev,
      sortOptions
    }));
  };

  const setSearchQuery = (searchQuery: string) => {
    setState(prev => ({
      ...prev,
      searchQuery
    }));
  };

  const contextValue: AppContextType = {
    state,
    actions: {
      loadData,
      signIn,
      signInWithSSO,
      signInWithAzure,
      signInWithOAuth,
      signUp,
      signOut,
      updateProfile,
      addIdea,
      updateIdea,
      deleteIdea,
      addRoadmapItem,
      updateRoadmapItem,
      deleteRoadmapItem,
      addTask,
      updateTask,
      deleteTask,
      voteOnIdea,
      addComment,
      addNotification,
      triggerAIAnalysis,
      setViewMode,
      setFilters,
      setSortOptions,
      setSearchQuery,
    },
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within a SupabaseAppProvider');
  }
  return context;
}; 